import React, { useState } from 'react';
import { Modal } from '@routific/hive';
import { Box } from '@mui/material';
import styled from 'styled-components';
import { Controller, Control, FieldValues } from 'react-hook-form';
import DateRangePicker, { DateRangeValidationError } from '../../shared/DatePickerRange';
import DownloadCSVAlert from './DownloadCSVAlert';
import { DownloadCSVAPIError } from './types';
import DownloadCSVModalTitle from './DownloadCSVModalTitle';
import ExportButton from './ExportButton';
import CancelButton from './CancelButton';
import { DOWNLOAD_CSV_DATE_RANGE_LIMIT } from '../../shared/DatePickerRange/helpers/constants';
import { DateTime } from 'luxon';
import LoadingText from './LoadingText';
import ColumnSelector from './ColumnSelector';
import { ExportColumnGroup } from '../../../store/mst/models';
import { ExportColumnGroupData } from './ColumnSelector/types';

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const FooterActions = styled.div`
  margin-left: auto;
`;

interface Props {
  visible: boolean;
  onCancel: () => void;
  onOk: () => void;
  downloadError: DownloadCSVAPIError | null;
  downloadErrorLimit?: number;
  // control/clearDownloadError/defaultValue: pass all three to render the date range picker (bulk flow); omit all three to skip it (single-route flow).
  control?: Control<FieldValues, any>;
  clearDownloadError?: () => void;
  queryRouteStats?: (startDate: string | undefined, endDate: string | undefined) => Promise<any>;
  defaultValue?: [DateTime | null, DateTime | null];
  dayHasRoutes?: (dayToCheck: any) => boolean,
  isLoading: boolean,
  groups: ExportColumnGroupData[];
  selectedKeys: Set<string>;
  hasSelection: boolean;
  hasError: boolean;
  toggleColumn: (key: string) => void;
  toggleGroup: (group: ExportColumnGroup) => void;
  isGroupChecked: (group: ExportColumnGroup) => boolean;
  isGroupIndeterminate: (group: ExportColumnGroup) => boolean;
}


const DownloadCSVModal: React.FC<Props> = ({
  visible, onCancel, onOk, control, downloadError, downloadErrorLimit, clearDownloadError, queryRouteStats, dayHasRoutes, isLoading, defaultValue,
  groups, selectedKeys, hasSelection, hasError, toggleColumn, toggleGroup, isGroupChecked, isGroupIndeterminate,
}) => {
  const [inputError, setInputError] = useState<DateRangeValidationError | null>(null);
  const showAlert = !!inputError || !!downloadError;

  const onInputError = (err: DateRangeValidationError | null) => {
    if (err && downloadError) {
      clearDownloadError?.();
    }
    setInputError(err);
  };

  return (
    <Modal
      title={
        <DownloadCSVModalTitle />
      }
      visible={visible}
      onCancel={onCancel}
      onOk={onOk}
      centered
      destroyOnClose
      maskClosable={false}
      footer={[
        <ModalFooter key="footer">
          {isLoading && <LoadingText />}
          <FooterActions>
            <CancelButton onCancel={onCancel} />
            <ExportButton control={control} onOk={onOk} isInputError={!!inputError} isLoading={isLoading} hasSelection={hasSelection} />
          </FooterActions>
        </ModalFooter>,
      ]}
    >
      <Box display="flex" gap={2} flexDirection="column">
        {control && (
          <form>
            <Controller
              name="dateRange"
              control={control}
              defaultValue={defaultValue}
              render={({ field }) => (
                <DateRangePicker
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  onError={onInputError}
                  dateRangeLimit={DOWNLOAD_CSV_DATE_RANGE_LIMIT}
                  queryFn={queryRouteStats}
                  defaultValue={defaultValue}
                  dayHasRoutes={dayHasRoutes}
                />
              )}
            />
          </form>
        )}
        <ColumnSelector
          groups={groups}
          selectedKeys={selectedKeys}
          hasSelection={hasSelection}
          hasError={hasError}
          toggleColumn={toggleColumn}
          toggleGroup={toggleGroup}
          isGroupChecked={isGroupChecked}
          isGroupIndeterminate={isGroupIndeterminate}
        />
        {showAlert && <DownloadCSVAlert alertType={inputError || downloadError} orderLimit={downloadErrorLimit} />}
      </Box>
    </Modal>
  );
};

export default DownloadCSVModal;
